<template>
  <div class="page-container">
    <PageHeader title="积分记录" show-breadcrumb />

    <!-- 搜索面板：按用户ID/积分描述筛选 -->
    <SearchPanel>
      <el-input v-model.trim="query.zauId" placeholder="用户ID" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-input v-model.trim="query.puprPointsDes" placeholder="积分描述" clearable size="small" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="用户积分记录列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column label="用户" min-width="140" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.zauName || ('用户' + row.zauId) }}</template>
        </el-table-column>
        <el-table-column label="积分变动" min-width="100" align="center">
          <template slot-scope="{ row }">
            <span :class="pointsCls(row.puprPoints)">{{ pointsText(row.puprPoints) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="puprPointsDes" label="积分描述" min-width="240" align="center" show-overflow-tooltip />
        <el-table-column label="创建时间" min-width="160" align="center">
          <template slot-scope="{ row }">{{ formatTime(row.createtime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 积分记录详情 -->
    <el-dialog title="积分记录详情" :visible.sync="detailVisible" width="480px" v-dialog-drag-resize>
      <div v-loading="detailLoading" class="points-detail">
        <template v-if="detail">
          <div class="stat-row"><span>用户</span><b>{{ detail.zauName || ('用户' + detail.zauId) }}</b></div>
          <div class="stat-row"><span>积分变动</span><b :class="pointsCls(detail.puprPoints)">{{ pointsText(detail.puprPoints) }}</b></div>
          <div class="stat-row"><span>积分描述</span><b>{{ detail.puprPointsDes || '-' }}</b></div>
          <div class="stat-row"><span>关联试卷ID</span><b>{{ detail.qpId != null ? detail.qpId : '-' }}</b></div>
          <div class="stat-row"><span>关联答题ID</span><b>{{ detail.qprId != null ? detail.qprId : '-' }}</b></div>
          <div class="stat-row"><span>创建时间</span><b>{{ formatTime(detail.createtime) }}</b></div>
        </template>
      </div>
      <div slot="footer"><el-button size="small" @click="detailVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getPkqUserPointsRecordList, getPkqUserPointsRecordDetails } from '@/api/api'

export default {
  name: 'PkqUserPointsRecord',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { zauId: '', puprPointsDes: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      detailVisible: false,
      detailLoading: false,
      detail: null
    }
  },
  created () { this.fetchList() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    // 积分实体未加 @JsonFormat，可能返回 ISO 格式(带T)，统一转成 yyyy-MM-dd HH:mm:ss 展示
    formatTime (t) {
      if (!t) return '-'
      return String(t).replace('T', ' ').replace(/\.\d+$/, '')
    },
    // 积分变动样式：加分绿色、扣分红色、0 灰色
    pointsCls (v) {
      const n = Number(v) || 0
      if (n > 0) return 'is-plus'
      if (n < 0) return 'is-minus'
      return 'is-zero'
    },
    pointsText (v) {
      const n = Number(v) || 0
      return n > 0 ? '+' + n : String(n)
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        // 只传有值的筛选条件，避免空串传给后端数字参数
        const params = { currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        if (this.query.zauId) params.zauId = this.query.zauId
        if (this.query.puprPointsDes) params.puprPointsDes = this.query.puprPointsDes
        const res = await getPkqUserPointsRecordList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { zauId: '', puprPointsDes: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    async handleDetail (row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const res = await getPkqUserPointsRecordDetails({ puprId: row.puprId })
        this.detail = res.data || null
      } catch (e) { this.detail = null } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.points-detail { min-height: 120px; }
.stat-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; border-bottom: 1px dashed #e5e7eb;
  font-size: 14px;
  span { color: #64748b; }
  b { color: #1e293b; font-size: 15px; }
}
/* 积分变动颜色 */
.is-plus { color: #16a34a; font-weight: 700; }
.is-minus { color: #dc2626; font-weight: 700; }
.is-zero { color: #94a3b8; }
</style>
